/**
 * The window's own view API, for the desktop shell to call.
 *
 * The agent's tools run in the daemon, which is a sibling process with no way to
 * touch this window. The shell is the one thing that can reach both: it spawned
 * the daemon and it owns the BrowserWindow. So the daemon asks the shell, and the
 * shell calls in here.
 *
 * WHY NOT THROUGH A DOCUMENT. Because the panel layout is not project state. It
 * is a property of THIS window — which panels you like where — and putting it in a
 * synced document to get it in front of the agent would make everyone in a
 * project share one arrangement, and would drag in replay, staleness and
 * identity problems that only exist because the transport was wrong. The layout
 * stays in localStorage where it was; the agent gets a door to it.
 *
 * DESKTOP ONLY, honestly. A browser tab talking to a hosted worker has no shared
 * process tree and so no door; `read` there simply never gets called. That is a
 * real limit rather than a hidden one: the capability exists where a window and a
 * daemon are in one process tree, and the tools that use it live in mod-desktop
 * for the same reason.
 */

import {
  type Content,
  type ContentNode,
  leaves,
  openCodeTab,
  openLeaf,
} from '../../features/code/contentTree'
import { webFileFor } from '../../features/web/webPage'
import type { AppControlRequest } from '../../services/apps/appControl'
import { decodeContent, encodeContent } from './contentUrl'

export interface AgentView {
  /** The document on the canvas, or null when it is empty. */
  openDocId: string | null
  /** The panes on screen, described in visual order. */
  panes: string[]
  /** The named arrangement, or "Custom" once it has been dragged. */
  layout: string
  /**
   * The space this window is in, by NAME (RFC 0281 S6), or null if the World
   * has none. A name rather than an id because it is what a person would say
   * back — "put me in the review space" is the ask this exists to serve.
   */
  space?: string | null
  /** Every space's name, so the agent can say which ones there are. */
  spaces?: string[]
  /**
   * The sessions open in a pane of the space on screen (RFC 0348 3b). The
   * daemon reads it before telling the World agent a session stopped: one the
   * person is looking at is not news. Optional, since the shell can load an
   * older deployed app, which then reports every stop.
   */
  sessions?: string[]
}

export interface AgentViewApi {
  read(): AgentView
  /**
   * Open a document by id, or close the focused pane with null.
   *
   * FALSE when the id resolves to nothing this window can draw — an unknown
   * id, or a record whose app keeps its own index so its instances are not
   * openable on their own (RFC 0271). Nothing moves in that case.
   *
   * It returned void and opened anything. A record that resolves to nothing
   * drew an EMPTY pane, and a centre open replaces, so opening one destroyed
   * whatever the pane held and reported success. The same rule `mod.window`
   * has always stated: refuse rather than approximate.
   */
  open(docId: string | null): boolean
  /**
   * Open a SESSION in a pane beside the focused one.
   *
   * Separate from `open` rather than resolving one id against both catalogs: a
   * document id and a session id look alike, and a lookup that guessed wrong
   * would open the wrong thing rather than say it could not find it. False when
   * this project has no such session.
   */
  openSession?(sessionId: string): boolean
  /**
   * Send one app-defined command to the frame in the FOCUSED pane, or, with
   * `documentId`, to the pane showing that document in any space, without
   * switching the window (every space's panes stay mounted). False when no
   * such pane is a mounted app view with a command surface.
   */
  controlApp?(
    request: AppControlRequest & { documentId?: string },
  ): Promise<unknown | false>
  /**
   * Open something in the space the CALLING SESSION is in, rather than in
   * whichever space this window happens to be showing.
   *
   * A session lives in a space: it is an `agent` leaf in that space's content
   * tree. `open` and `openSession` are told what to open and nothing else, so
   * an agent working in one space pushes a pane into whatever the user is
   * reading somewhere else, which from their side looks like being reached
   * into rather than answered.
   *
   * `sessionId` is who is asking. Exactly one of `docId` and `openSessionId`
   * says what to open — one method rather than two because the hard part is
   * finding the home space and getting there, and that part is the same for
   * both.
   *
   * False when no space holds the calling session, or when `openSessionId`
   * names a session this project does not have. The tool reads false as
   * "open it where the window already is" and falls back, which is the honest
   * answer: there is no better home to send it to.
   */
  openForSession?(req: {
    sessionId: string
    docId?: string
    openSessionId?: string
  }): boolean
  /**
   * Open a web page for an agent's browser_open, in the CALLING SESSION's
   * space, by the same rule as `openForSession`.
   *
   * `window` means it opened here, split beside what is on screen, and its
   * pane mounts now. `space` means it was written into the session's space,
   * which is not on screen, so its pane mounts only when someone goes there.
   * False when no space holds the session or the url is not a web page: the
   * shell then opens it the old way, in the window.
   */
  openWebForSession?(req: {
    sessionId: string
    url: string
  }): WebOpenPlacement | false
  /**
   * Is this window the one for this session: its conversation is a pane on
   * screen here, or the space on screen is its home (`sessionHomeSpace`)?
   *
   * How the desktop shell picks which of several windows a session's view
   * request goes to (RFC 0343 3d). The home space counts because a window
   * showing it owns its live tree: writing that space's stored copy from
   * another window would never reach the screen, and the next edit here
   * would overwrite it.
   */
  showsSession?(sessionId: string): boolean
  /**
   * Focus a pane by its POSITION in `read()`'s `panes`, 1-based.
   *
   * By position because that is what the agent was just told. Leaf ids are
   * minted inside the tree and never leave it, so a position is the only handle
   * both sides share.
   */
  focusPane?(index: number): boolean
  /** Close a pane by the same 1-based position. False when there is no such pane. */
  closePane?(index: number): boolean
  /** Apply a named arrangement. Unknown names are ignored, not guessed at. */
  setLayout(name: string): boolean
  /**
   * Go to a space BY NAME (RFC 0281 S6). False when no space has that name,
   * so the agent is told rather than left believing the window moved.
   *
   * By name, not index: a digit means a position, and a position is a fact
   * about one device's strip (§7). An agent saying "space 2" would mean
   * something different depending on whose window it reached.
   */
  setSpace?(name: string): boolean
  /**
   * Make a space with this name and go to it. Returns the name it got, or
   * false when the World's spaces have not loaded yet.
   *
   * The name is required here, unlike ⌘T's "Space 4": an agent opening a space
   * knows what the work is, and a space nobody named is one nobody can switch
   * back to by name.
   */
  createSpace?(name: string): string | false
  /** Rename a space, `from` its current name. False when no space is called that. */
  renameSpace?(from: string, to: string): boolean
  /**
   * Close a space by name.
   *
   * Closing destroys nothing (RFC 0281 §7): every session and document it named
   * is still in its project, and the tree it held was a view. False when no
   * space has that name, or when it is the last one, which is not closable.
   */
  closeSpace?(name: string): boolean
  /**
   * What a web pane should say (RFC 0303 3e): driving until when, by which
   * session, and whether the person has taken it. Pushed from the shell after
   * every action and every Take over or Hand back, because the actions happen
   * outside the page and the take-over latch is the shell's, on the page.
   * Named by the guest's webContents id, which the pane's `<webview>` knows.
   */
  noteWebPage?(
    webContentsId: number,
    state: { drivingUntil: number; held: boolean; sessionId?: string | null },
  ): void
}

/**
 * Find a space by the name an agent said, trimmed and case-insensitive.
 *
 * One matcher for every space verb, because switching, renaming and closing
 * must agree on what "the chrome space" means. A close that resolved a name
 * differently from the switch that preceded it would delete the wrong space.
 */
export function findSpaceByName<T extends { name: string }>(
  spaces: readonly T[],
  name: string,
): T | undefined {
  const wanted = name.trim().toLowerCase()
  if (!wanted) return undefined
  return spaces.find((sp) => sp.name.trim().toLowerCase() === wanted)
}

/**
 * What `openForSession` should do, decided before anything is touched.
 *
 * `window`   the calling session is in the space on screen, so open it there
 *            through the layout, against the tree the url is carrying.
 * `space`    it lives somewhere else. `content` is that space's tree with the
 *            pane already in it, to be written to its document. The window does
 *            NOT go there: it is showing what someone is reading.
 * `nowhere`  no space holds that session, or there is nothing to place. The
 *            caller must answer FALSE and let the tool fall back to a plain
 *            open, rather than report a change nobody could see.
 */
export type OpenForSessionPlan =
  | { at: 'window' }
  | { at: 'space'; spaceId: string; content: string }
  | { at: 'nowhere' }

/** Whether a tree has this session's conversation as one of its panes. */
export function treeHoldsSession(
  tree: ContentNode | null,
  sessionId: string,
): boolean {
  if (!sessionId) return false
  return leaves(tree).some(
    (l) => l.content.kind === 'agent' && l.content.sessionId === sessionId,
  )
}

/**
 * The space a session belongs to: the one whose tree holds its pane, else the
 * one it was last seen in (`noteSessionSpaces`), else null.
 *
 * The second step is "lean towards the space" (RFC 0343): a session whose pane
 * has closed still has somewhere of its own, rather than whatever the person
 * has switched to since.
 */
export function sessionHomeSpace(
  sessionId: string,
  spaces: readonly { spaceId: string; content: string }[],
  lastSpaceId: string | null,
): string | null {
  const holding = spaces.find((sp) =>
    treeHoldsSession(decodeContent(sp.content), sessionId),
  )
  if (holding) return holding.spaceId
  return spaces.some((sp) => sp.spaceId === lastSpaceId) ? lastSpaceId : null
}

/**
 * Decide where a pane opened FOR a session goes.
 *
 * PURE, and separate from the window, because the two ways this went wrong are
 * both decisions rather than effects. It once answered "opened" for a pane it
 * had only queued behind a navigation, and the navigation then cost the space
 * being left its unsaved tree. Nothing here moves the window, and every path
 * that cannot place the pane says so instead of guessing.
 */
export function planOpenForSession(args: {
  /** Who is asking. Its `agent` leaf is what marks a space as its home. */
  sessionId: string
  /** What to place, or null when the id named nothing this project has. */
  what: Content | null
  /** The tree on screen, which is ahead of any space's stored copy. */
  windowTree: ContentNode | null
  spaces: readonly { spaceId: string; content: string }[]
  currentSpaceId: string | null
  /**
   * The space this session was last seen in, from `noteSessionSpaces`. For a
   * session whose pane is in no tree now (closed, or never opened here), which
   * otherwise had nowhere to go but the space on screen.
   */
  lastSpaceId?: string | null
}): OpenForSessionPlan {
  const { sessionId, what, windowTree, spaces, currentSpaceId, lastSpaceId } =
    args
  if (!what || !sessionId) return { at: 'nowhere' }

  const holdsCaller = (tree: ContentNode | null) =>
    treeHoldsSession(tree, sessionId)

  // The window first, and from the LIVE tree: a session opened a moment ago is
  // in the url long before it is in the space's document, and reading the
  // stored copy would send its own pane to another space.
  if (holdsCaller(windowTree)) return { at: 'window' }

  const homeId = sessionHomeSpace(sessionId, spaces, lastSpaceId ?? null)
  const home = spaces.find((sp) => sp.spaceId === homeId)
  if (!home) return { at: 'nowhere' }
  // Its home IS the space on screen, and the stored copy was merely stale.
  if (home.spaceId === currentSpaceId) return { at: 'window' }

  // A code file joins the space's code pane as a tab, as it would on screen.
  // Otherwise `openLeaf` with no target splits BESIDE the whole tree rather
  // than replacing the root, so a space gains a pane and never loses one.
  const tree = decodeContent(home.content)
  const content = encodeContent(
    openCodeTab(tree, what, null)?.tree ?? openLeaf(tree, what, null),
  )
  if (!content) return { at: 'nowhere' }
  return { at: 'space', spaceId: home.spaceId, content }
}

/** Where `openWebForSession` put a page. `space` is the space's name. */
export type WebOpenPlacement = { at: 'window' } | { at: 'space'; space: string }

/**
 * Decide where an agent's web page goes: `planOpenForSession` for the web
 * document a url opens as. A url that is not http(s) names nothing, so it is
 * `nowhere`, the same as an unknown document id.
 */
export function planWebOpenForSession(
  args: Omit<Parameters<typeof planOpenForSession>[0], 'what'> & {
    url: string
    /** The project the pane is opened in. */
    projectId: string
  },
): OpenForSessionPlan {
  const { url, projectId, ...rest } = args
  const f = webFileFor(url)
  return planOpenForSession({
    ...rest,
    what: f ? { kind: 'document', projectId, docId: f.id } : null,
  })
}

/**
 * Remember which space each session's pane has been seen in.
 *
 * A space is a session's home only while its tree holds the session's pane.
 * Close the pane and the session has no home, and an open it makes afterwards
 * used to land in whatever the person was looking at. This keeps the last
 * space each one was seen in, so it still has somewhere of its own to go.
 * Never forgets an entry, only moves it: absent from every tree is exactly the
 * case it is for.
 */
export function noteSessionSpaces(
  memory: Map<string, string>,
  spaces: readonly { spaceId: string; content: string }[],
  currentSpaceId: string | null,
  windowTree: ContentNode | null,
): void {
  const note = (tree: ContentNode | null, spaceId: string) => {
    for (const l of leaves(tree))
      if (l.content.kind === 'agent' && l.content.sessionId)
        memory.set(l.content.sessionId, spaceId)
  }
  for (const sp of spaces)
    if (sp.spaceId !== currentSpaceId)
      note(decodeContent(sp.content), sp.spaceId)
  // The live tree last, so it wins over the space's stored copy.
  if (currentSpaceId) note(windowTree, currentSpaceId)
}

/** Where the shell looks for it. A single global, because `executeJavaScript`
 *  evaluates in the page and has no module scope to import from. */
const GLOBAL = '__modAgentView'

/**
 * Publish the API for the shell, and take it down on unmount.
 *
 * Returns a cleanup function rather than registering forever: a hot reload or a
 * second mount would otherwise leave a stale closure answering for a window that
 * has moved on, and the agent would read a view nobody is looking at.
 */
export function publishAgentView(api: AgentViewApi): () => void {
  const target = globalThis as unknown as Record<string, unknown>
  target[GLOBAL] = api
  return () => {
    if (target[GLOBAL] === api) delete target[GLOBAL]
  }
}

export const AGENT_VIEW_GLOBAL = GLOBAL
